import React, {FC, useState, useEffect} from "react"

import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {H2, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import Link, {RouterLink, ButtonLink} from "@irati/ui/src/Link"
import adminTracking from "@irati-tracking/admin"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Button from "@irati/ui/src/Button"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"

import {VIEW_PEOPLE} from "../../../../../../routes/routeConfig"
import {Img} from "../styled"
import AbandonModal from "./AbandonModal"
import {ProviderLabel} from "./styled"

interface Provider {
  label: string
  img: string
  checked: boolean
}

const HRIS_PROVIDERS: Provider[] = [
  {
    label: "azure",
    img: "admin.components.integration.directorySync.hris.providers.img1",
    checked: false
  },
  {
    label: "google",
    img: "admin.components.integration.directorySync.hris.providers.img2",
    checked: false
  },
  {
    label: "okta",
    img: "admin.components.integration.directorySync.hris.providers.img3",
    checked: false
  },
  {
    label: "bambooHr",
    img: "admin.components.integration.directorySync.hris.providers.img4",
    checked: false
  },
  {
    label: "bob",
    img: "admin.components.integration.directorySync.hris.providers.img5",
    checked: false
  },
  {
    label: "cezanne",
    img: "admin.components.integration.directorySync.hris.providers.img6",
    checked: false
  },
  {
    label: "personio",
    img: "admin.components.integration.directorySync.hris.providers.img7",
    checked: false
  },
  {
    label: "accessPeople",
    img: "admin.components.integration.directorySync.hris.providers.img8",
    checked: false
  },
  {
    label: "workDay",
    img: "admin.components.integration.directorySync.hris.providers.img9",
    checked: false
  },
  {
    label: "jumpcloud",
    img: "admin.components.integration.directorySync.hris.providers.img10",
    checked: false
  },
  {
    label: "rippling",
    img: "admin.components.integration.directorySync.hris.providers.img11",
    checked: false
  },
  {
    label: "oneLogin",
    img: "admin.components.integration.directorySync.hris.providers.img12",
    checked: false
  },
  {
    label: "pinkFederate",
    img: "admin.components.integration.directorySync.hris.providers.img13",
    checked: false
  },
  {
    label: "cyberark",
    img: "admin.components.integration.directorySync.hris.providers.img14",
    checked: false
  },
  {
    label: "SCIM_2.0",
    img: "admin.components.integration.directorySync.hris.providers.img15",
    checked: false
  },
  {
    label: "SCIM_1.1",
    img: "admin.components.integration.directorySync.hris.providers.img16",
    checked: false
  }
]

interface Props extends InjectIntl {
  show: boolean
  onClose: () => void
  onNotFound: () => void
  onNext: (provider?: string) => void
}

const SelectProviderModal: FC<Props> = ({show, onClose, onNotFound, onNext, formatMessage}) => {
  const [providers, setProviders] = useState<Provider[]>(HRIS_PROVIDERS)
  const [selectedProvider, setSelectedProvider] = useState<string>()
  const [alertError, setAlertError] = useState(false)
  const [showModal, setShowModal] = useState(show)
  const [showAbandonModal, setShowAbandonModal] = useState(false)

  const theme = useTheme()

  const handleOnClick = (label: string) => {
    setAlertError(false)
    const updateProviders = providers.map((item) => {
      return {...item, checked: label === item.label}
    })
    setProviders(updateProviders)
    setSelectedProvider(label)
  }

  const handleHRISNotFound = () => {
    onClose()
    onNotFound()
  }

  const handleNextStep = () => {
    if (!selectedProvider) {
      setAlertError(true)
    } else {
      adminTracking.hrisUpsellSetup("admin_dysnc_purchase_select_provider", {
        source: "Navigation",
        viewType: "Default",
        action: selectedProvider
      })
      onClose()
      onNext(selectedProvider)
    }
  }

  const handleOnReturn = () => {
    setShowAbandonModal(false)
    setShowModal(true)
  }

  const handleOnClose = () => {
    setShowAbandonModal(false)
    onClose()
  }

  const handleOnHide = () => {
    setShowModal(false)
    setShowAbandonModal(true)
  }

  useEffect(() => {
    setShowModal(show)
    setProviders(HRIS_PROVIDERS)
    setSelectedProvider("")
  }, [show])

  return (
    <>
      <GenericModal customWidth={750} hideActions show={showModal} onHide={handleOnHide}>
        <H2 marginBottom={1}>
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.step1.title" />
        </H2>
        {alertError && (
          <Alert type={AlertTypes.error} title="admin.components.integration.directorySync.hris.upsell.step1.validationChecklist" />
        )}
        <Body maxWidth="100%">
          <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.step1.subTitle" />
        </Body>
        <Flex wrap="wrap" gap={[2, 4]} padding={2}>
          {providers.slice(0, providers.length - 2).map((item) => (
            <ProviderLabel
              justifyContent="center"
              alignItems="center"
              padding={[theme.spacing.oneAndHalf, theme.spacing.two]}
              selected={item.checked}
              onClick={() => handleOnClick(item.label)}>
              <Img width={theme.spacing.twelve} height="auto" src={formatMessage({id: item.img})} />
            </ProviderLabel>
          ))}
        </Flex>
        <Flex padding={2}>
          <Body small maxWidth="100%">
            <FormattedMessage
              id="admin.components.integration.directorySync.hris.upsell.step1.scim"
              values={{
                source: (
                  <Link
                    cta
                    href={formatMessage({id: "admin.components.integration.directorySync.hris.upsell.step1.scimLink"})}
                    target="_blank">
                    <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.step1.scimSource" />
                  </Link>
                )
              }}
            />
          </Body>
        </Flex>
        <Flex wrap="wrap" gap={4} padding={2}>
          {providers.slice(providers.length - 2, providers.length).map((item) => (
            <ProviderLabel
              justifyContent="center"
              alignItems="center"
              padding={[theme.spacing.half, theme.spacing.oneAndHalf]}
              selected={item.checked}
              onClick={() => handleOnClick(item.label)}>
              <Img height={theme.spacing.five} src={formatMessage({id: item.img})} />
            </ProviderLabel>
          ))}
        </Flex>
        <Flex justifyContent="space-between" alignItems="baseline">
          <RouterLink
            tertiary
            to={VIEW_PEOPLE}
            onClick={() => adminTracking.hrisUpsellSetup("admin_dysnc_purchase_IT_help", {action: "IT_help"})}>
            <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.step1.inviteIT" />
          </RouterLink>
          <Flex gap={3} alignItems="baseline">
            <ButtonLink tertiary onClick={handleHRISNotFound}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.step1.hrisNotHere" />
            </ButtonLink>
            <Button primary onClick={handleNextStep}>
              <FormattedMessage id="admin.components.integration.directorySync.hris.upsell.step1.next" />
            </Button>
          </Flex>
        </Flex>
      </GenericModal>
      <AbandonModal show={showAbandonModal} upsell onClose={handleOnClose} onReturn={handleOnReturn} />
    </>
  )
}

export default injectIntl(SelectProviderModal)
